import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { dissoc, mergeDeepRight, mergeRight, propEq, reject } from "ramda";
import { setLanguages as mapLanguages } from "../edit/ProjectLanguagesDialog";

import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { isBlank, mapIndexed, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import makeStyles from "@mui/styles/makeStyles";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import EditIcon from "@mui/icons-material/Edit";
import ProjectLanguagesDialog from "../edit/ProjectLanguagesDialog";
import { useLanguages } from "../../creator-application/vocabularyServices";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

const ProjectLanguagesCard = ({ project, loading, onUpdate }) => {
  const classes = useStyles();
  const apiLanguages = useLanguages();

  // map languages to Stip.ProjectLanguage
  const [languages, setLanguages] = useState([]);
  useEffect(() => {
    if (apiLanguages.isSuccess) {
      const languages = mapIndexed(
        (lang, idx) => ({
          code: lang.isoCode639_2,
          name: lang.name,
          order: idx,
          id: lang.isoCode639_2,
        }),
        apiLanguages.data,
      );
      setLanguages(languages);
    }
  }, [apiLanguages.isSuccess, apiLanguages.data]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const handleAddClick = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleDialogApply = useCallback(
    async data => {
      setDialogOpen(false);
      const update = mergeRight(project, {
        languages: mapIndexed(dissoc("id"), data),
      });

      onUpdate(update);
    },
    [project, onUpdate],
  );

  const handleDeleteClick = useCallback(
    async lang => {
      const languages = reject(propEq("code", lang.code), project.languages);
      const update = mergeDeepRight(project, {
        languages: mapLanguages(languages),
      });

      onUpdate(update);
    },
    [project, onUpdate],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "h6",
          color: "primary",
        }}
        title="Language Options"
        action={
          <Tooltip title="Add Languages">
            <IconButton onClick={handleAddClick} size="large">
              <EditIcon color="primary" fontSize="large" />
            </IconButton>
          </Tooltip>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {loading && <PanelLoadingIndicator />}
        {isBlank(project) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            Error Loading Languages Options.
          </Box>
        )}

        {isBlank(project.languages) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            No Languages are set up for this Project.
          </Box>
        )}

        {notBlank(project.languages) && (
          <List dense style={{ maxHeight: 400, overflowY: "auto" }}>
            {mapIndexed(
              (lang, idx) => (
                <ListItem key={idx}>
                  <ListItemText primary={lang.name} />
                  <ListItemSecondaryAction>
                    <ConfirmButton
                      handleDelete={() => handleDeleteClick(lang)}
                      dialogTitle="Remove Language"
                      dialogBody="Are you sure you want to remove this language?"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ),
              project.languages,
            )}
          </List>
        )}
      </CardContent>
      {dialogOpen && apiLanguages.isSuccess && (
        <ProjectLanguagesDialog
          project={project}
          languages={languages}
          open={dialogOpen}
          onCancel={handleCancel}
          onApply={handleDialogApply}
        />
      )}
    </Card>
  );
};

export default ProjectLanguagesCard;
